import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api, ApiError } from '../api/client';

export type UserRole = 'customer' | 'carrier' | 'moderator' | 'support' | 'finance' | 'admin';

export interface SessionUser {
  id: number;
  email: string;
  phone: string;
  display_name: string;
  active_role: UserRole;
  phone_verified_at: string | null;
  email_verified_at: string | null;
  has_customer_profile: boolean;
  has_carrier_profile: boolean;
  available_roles: UserRole[];
  linked_services?: string[];
}

export type SessionStatus = 'loading' | 'guest' | 'authenticated' | 'error';

interface AuthContextValue {
  deleteAccount: () => Promise<void>;
  error: ApiError | Error | null;
  refresh: () => Promise<SessionUser | null>;
  setAuthenticatedUser: (user: SessionUser) => void;
  signOut: () => Promise<void>;
  status: SessionStatus;
  switchRole: (role: UserRole) => Promise<SessionUser>;
  user: SessionUser | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const AUTH_CACHE_KEY = 'lovo_cached_user';

function getCachedUser(): SessionUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => getCachedUser());
  const [status, setStatus] = useState<SessionStatus>(() => (getCachedUser() ? 'authenticated' : 'loading'));
  const [error, setError] = useState<ApiError | Error | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const nextUser = await api<SessionUser>('/api/me/');
      setUser(nextUser);
      setStatus('authenticated');
      try {
        localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(nextUser));
      } catch {
        // ignore
      }
      return nextUser;
    } catch (nextError) {
      // Local dev override via ?dev=carrier or ?dev=customer (localhost only fallback if unauthenticated)
      if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        const devRole = new URLSearchParams(window.location.search).get('dev');
        if (devRole === 'carrier' || devRole === 'customer') {
          const mockUser: SessionUser = {
            id: devRole === 'carrier' ? 999 : 888,
            email: `${devRole}@lovo.ru`,
            phone: '+7 999 000-00-00',
            display_name: devRole === 'carrier' ? 'Иван Перевозчик' : 'Анна Заказчица',
            active_role: devRole,
            phone_verified_at: new Date().toISOString(),
            email_verified_at: new Date().toISOString(),
            has_customer_profile: true,
            has_carrier_profile: true,
            available_roles: ['carrier', 'customer'],
          };
          setUser(mockUser);
          setStatus('authenticated');
          return mockUser;
        }
      }
      if (nextError instanceof ApiError && (nextError.status === 401 || nextError.status === 403)) {
        try { localStorage.removeItem(AUTH_CACHE_KEY); } catch {}
        setUser(null);
        setStatus('guest');
        return null;
      }
      const normalized = nextError instanceof Error ? nextError : new Error(String(nextError));
      setUser(null);
      setError(normalized);
      setStatus('error');
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setAuthenticatedUser = useCallback((nextUser: SessionUser) => {
    setError(null);
    setUser(nextUser);
    setStatus('authenticated');
    try { localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(nextUser)); } catch {}
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api<void>('/api/auth/logout/', { method: 'POST' });
    } catch {}
    try { localStorage.removeItem(AUTH_CACHE_KEY); } catch {}
    setError(null);
    setUser(null);
    setStatus('guest');
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await api<void>('/api/auth/account/', {
        method: 'DELETE',
        body: { confirmation: 'DELETE' },
      });
    } catch {}
    try { localStorage.removeItem(AUTH_CACHE_KEY); } catch {}
    setError(null);
    setUser(null);
    setStatus('guest');
  }, []);

  const switchRole = useCallback(async (role: UserRole) => {
    const nextUser = await api<SessionUser>('/api/auth/active-role/', {
      method: 'POST',
      body: { active_role: role },
    });
    setAuthenticatedUser(nextUser);
    return nextUser;
  }, [setAuthenticatedUser]);

  const value = useMemo<AuthContextValue>(
    () => ({ deleteAccount, error, refresh, setAuthenticatedUser, signOut, status, switchRole, user }),
    [deleteAccount, error, refresh, setAuthenticatedUser, signOut, status, switchRole, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
